import React, { useEffect, useState } from "react";
import { MdOutlineChatBubbleOutline } from "react-icons/md";
import Accordion from "./Accordion";
import Table from "./AccordionTable";
import { _getPromptDetails, _updatePromptSteps } from "Services/api"; // Updated API
import SectionCard from "components/card/SectionCard";

const normalizePromptData = (data) => {
  const payload = data?.data || data || {};
  if (Array.isArray(payload)) return { prompts: normalizePromptList(payload) };

  const prompts = Array.isArray(payload?.prompts) ? payload.prompts : [];
  const flows = Array.isArray(payload?.flows) ? payload.flows : [];

  return {
    prompts: normalizePromptList([...prompts, ...flows]),
  };
};

const normalizeStep = (step) => ({
  ...step,
  step_content: step?.step_content ?? step?.question ?? "",
  step_order: step?.step_order ?? step?.step_id,
  options: Array.isArray(step?.options) ? step.options : [],
});

const normalizePrompt = (prompt) => ({
  ...prompt,
  prompt_id: prompt?.prompt_id ?? prompt?.flow_id,
  prompt_name: prompt?.prompt_name ?? prompt?.flow_name ?? "",
  steps: Array.isArray(prompt?.steps) ? prompt.steps.map(normalizeStep) : [],
});

const getPromptKey = (prompt) =>
  prompt?.prompt_id ||
  prompt?.flow_id ||
  prompt?.prompt_key ||
  prompt?.flow_key ||
  prompt?.prompt_name;

const normalizePromptList = (items) => {
  const seen = new Set();

  return items.map(normalizePrompt).filter((prompt) => {
    const key = getPromptKey(prompt);
    if (!key) return true;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

const addDefined = (target, key, value) => {
  if (value !== undefined) {
    target[key] = value;
  }
};

const AccordionComponent = () => {
  const [openAccordionIndex, setOpenAccordionIndex] = useState(null);
  const [promptData, setPromptData] = useState({ prompts: [] });
  const [loading, setLoading] = useState(true);

  const botDetails = JSON.parse(sessionStorage.getItem("botData"));
  const botId = botDetails?.unique_id || botDetails?.bot_id;
  const accessToken = sessionStorage.getItem("VendorToken");

  useEffect(() => {
    const fetchPromptDetails = async () => {
      if (!botId) {
        setLoading(false);
        return;
      }

      setLoading(true);
      try {
        const response = await _getPromptDetails({ accessToken, botId });
        if (response.status) {
          setPromptData(normalizePromptData(response.data)); // Set prompt data from API response
        }
      } catch (error) {
        console.error("Error fetching prompt details:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchPromptDetails();
  }, [accessToken, botId]);

  // Function to handle step content update
  const handleUpdateStep = async (updatedStep) => {
    const { prompt_id, step_id, step_content, options } = updatedStep;
    const body = {
      question: step_content, // Send the updated content as 'question'
      options: Array.isArray(options) ? options : [],
    };
    addDefined(body, "input_key", updatedStep.input_key);
    addDefined(body, "api_call", updatedStep.api_call);
    addDefined(body, "api_type", updatedStep.api_type);
    addDefined(body, "condition", updatedStep.condition);
    addDefined(body, "values_to_replace", updatedStep.values_to_replace);
    addDefined(body, "end_conversation", updatedStep.end_conversation);

    try {
      const response = await _updatePromptSteps({
        prompt_id, // Pass the prompt_id
        step_id, // Pass step_order as step_id
        body, // Send the updated content in the body
        accessToken, // Pass the accessToken
      });

      if (response.status) {
        const savedStep = normalizeStep(response.data || updatedStep);
        // Update the prompt data in the state with the new content
        setPromptData((prevData) => {
          const currentData = normalizePromptData(prevData);
          return {
            ...currentData,
            prompts: currentData.prompts.map((prompt) =>
              prompt.prompt_id === prompt_id
                ? {
                    ...prompt,
                    steps: (Array.isArray(prompt.steps) ? prompt.steps : []).map((step) =>
                      step.step_order === updatedStep.step_order
                        ? { ...updatedStep, ...savedStep }
                        : step
                    ),
                  }
                : prompt
            ),
          };
        });
      }
    } catch (error) {
      console.error("Error updating step content:", error);
    }
  };

  const handleAccordionClick = (index) => {
    setOpenAccordionIndex(openAccordionIndex === index ? null : index);
  };

  return (
    <SectionCard
      title="Customize Message"
      description="Edit the flow steps and options your chatbot uses to guide guests."
      icon={<MdOutlineChatBubbleOutline />}
    >
      {loading ? (
        <p className="text-sm text-gray-500">Loading...</p>
      ) : promptData.prompts.length > 0 ? (
        promptData.prompts.map((prompt, index) => (
          <Accordion
            key={index}
            title={prompt.prompt_name}
            isOpen={openAccordionIndex === index}
            onClick={() => handleAccordionClick(index)}
          >
            <Table
              data={prompt.steps} // Ensure prompt.steps contains step_id
              prompt_id={prompt.prompt_id} // Pass prompt_id as a prop
              onUpdateStep={handleUpdateStep}
            />
          </Accordion>
        ))
      ) : (
        <p className="text-sm text-gray-500">No prompt or flow messages found.</p>
      )}
    </SectionCard>
  );
};

export default AccordionComponent;
